/**
 * ============================================================================
 * ProcessOperation 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 ProcessOperation（工序）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createProcessOperationApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   put: (url, data, config) => requestClient.put(url, data, config).then(res => res.data),
 *   delete: (url, config) => requestClient.delete(url, config).then(res => res.data),
 * }
 * 
 * import { createProcessOperationApi } from '@bayes-mes/shared/api/process-operation/factory'
 * export const processOperationApi = createProcessOperationApi(webAdapter)
 * ```
 * 
 * @module api/process-operation/factory
 * @description ProcessOperation 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-31
 * @last-updated 2025-10-31
 * 
 * @change-log
 * - 2025-10-31: 初始创建，实现所有基础 CRUD 和业务方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  ProcessOperation,
  ProcessOperationQueryParams,
  ProcessOperationAllQueryParams,
  CreateProcessOperationForm,
  UpdateProcessOperationForm,
  BatchDeleteProcessOperationForm,
  SetOperationFlowForm,
  ReorderOperationsForm,
  BatchImportOperationsForm,
  DuplicateOperationForm,
  UpdateOperationStatusForm,
  CalculateOperationOutputForm,
  CalculateRequiredInputForm,
  ProcessOperationStatistics,
  ProcessOperationGlobalStatistics,
} from '../../types/process-operation'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入请求客户端接口 =====
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 工厂函数定义 =====
/**
 * 创建 ProcessOperation 模块 API
 * 
 * @param client 请求客户端（符合 IRequestClient 接口）
 * @returns ProcessOperation 模块的所有 API 方法
 */
export function createProcessOperationApi(client: IRequestClient) {
  return {
    // ========================================
    // 查询相关 API
    // ========================================

    /**
     * 获取工序列表（分页）
     * 
     * @description
     * 获取分页的工序列表，支持多种筛选条件。
     * 
     * @param params 查询参数
     * @returns 分页的工序列表
     * 
     * @backend ProcessOperationController::index()
     */
    getProcessOperationList: (params?: ProcessOperationQueryParams) =>
      client.get<PaginationResult<ProcessOperation>>(API_ROUTES.processOperations.list, { params }),

    /**
     * 获取所有工序（不分页）
     * 
     * @description
     * 获取所有工序，不分页返回，适用于下拉选择等场景。
     * 
     * @param params 查询参数
     * @returns 工序列表
     * 
     * @backend ProcessOperationController::all()
     */
    getAllProcessOperations: (params?: ProcessOperationAllQueryParams) =>
      client.get<ProcessOperation[]>(API_ROUTES.processOperations.all, { params }),

    /**
     * 获取工序详情
     * 
     * @description
     * 根据 ID 获取工序的详细信息。
     * 
     * @param id 工序ID
     * @returns 工序详情
     * 
     * @backend ProcessOperationController::show()
     */
    getProcessOperationDetail: (id: number) =>
      client.get<ProcessOperation>(API_ROUTES.processOperations.processOperations(id)),

    /**
     * 根据工序编码查询
     * 
     * @description
     * 通过工艺路线ID和工序编码查找工序。
     * 
     * @param routeId 工艺路线ID
     * @param code 工序编码
     * @returns 工序信息
     * 
     * @backend ProcessOperationController::findByCode()
     */
    findProcessOperationByCode: (routeId: number, code: string) =>
      client.get<ProcessOperation>(API_ROUTES.processOperations.findByCode(routeId, code)),

    /**
     * 获取工艺路线的所有工序
     * 
     * @description
     * 获取指定工艺路线的所有工序列表（按序号排序）。
     * 
     * @param routeId 工艺路线ID
     * @returns 工序列表
     * 
     * @backend ProcessOperationController::getOperationsByRoute()
     */
    getOperationsByRoute: (routeId: number) =>
      client.get<ProcessOperation[]>(API_ROUTES.processOperations.byRoute(routeId)),

    /**
     * 获取前一工序列表
     * 
     * @description
     * 获取可以作为指定工序的前一工序的列表。
     * 
     * @param id 工序ID
     * @returns 可用的前一工序列表
     * 
     * @backend ProcessOperationController::getPrevOperations()
     */
    getPrevOperations: (id: number) =>
      client.get<ProcessOperation[]>(API_ROUTES.processOperations.prevOperations(id)),

    /**
     * 获取后一工序列表
     * 
     * @description
     * 获取可以作为指定工序的后一工序的列表。
     * 
     * @param id 工序ID
     * @returns 可用的后一工序列表
     * 
     * @backend ProcessOperationController::getNextOperations()
     */
    getNextOperations: (id: number) =>
      client.get<ProcessOperation[]>(API_ROUTES.processOperations.nextOperations(id)),

    // ========================================
    // 创建和更新相关 API
    // ========================================

    /**
     * 创建工序
     * 
     * @description
     * 创建新的工序。
     * 
     * @param data 工序表单数据
     * @returns 创建的工序信息
     * 
     * @backend ProcessOperationController::store()
     */
    createProcessOperation: (data: CreateProcessOperationForm) =>
      client.post<ProcessOperation>(API_ROUTES.processOperations.create, data),

    /**
     * 更新工序
     * 
     * @description
     * 更新工序的基本信息，所有字段都是可选的。
     * 
     * @param id 工序ID
     * @param data 更新数据
     * @returns 更新后的工序信息
     * 
     * @backend ProcessOperationController::update()
     */
    updateProcessOperation: (id: number, data: UpdateProcessOperationForm) =>
      client.put<ProcessOperation>(API_ROUTES.processOperations.processOperations(id), data),

    /**
     * 删除工序
     * 
     * @description
     * 删除指定的工序（软删除）。
     * 
     * @param id 工序ID
     * @returns 操作结果
     * 
     * @backend ProcessOperationController::destroy()
     */
    deleteProcessOperation: (id: number) =>
      client.delete<void>(API_ROUTES.processOperations.delete(id)),

    /**
     * 批量删除工序
     * 
     * @description
     * 批量删除多个工序，返回成功删除的数量。
     * 
     * @param data 批量删除表单
     * @returns 操作结果，包含成功删除的数量
     * 
     * @backend ProcessOperationController::batchDestroy()
     */
    batchDeleteProcessOperation: (data: BatchDeleteProcessOperationForm) =>
      client.post<{ count: number }>(API_ROUTES.processOperations.batchDelete, data),

    // ========================================
    // 业务操作相关 API
    // ========================================

    /**
     * 设置工序流转
     * 
     * @description
     * 设置工序的前后工序关系。
     * 
     * @param id 工序ID
     * @param data 流转设置表单
     * @returns 更新后的工序信息
     * 
     * @backend ProcessOperationController::setOperationFlow()
     */
    setOperationFlow: (id: number, data: SetOperationFlowForm) =>
      client.put<ProcessOperation>(API_ROUTES.processOperations.flow(id), data),

    /**
     * 工序重新排序
     * 
     * @description
     * 批量更新工序的序号。
     * 
     * @param data 排序表单
     * @returns 操作结果
     * 
     * @backend ProcessOperationController::reorder()
     */
    reorderOperations: (data: ReorderOperationsForm) =>
      client.post<void>(API_ROUTES.processOperations.reorder, data),

    /**
     * 批量导入工序
     * 
     * @description
     * 批量导入工序到指定工艺路线。
     * 
     * @param data 批量导入表单
     * @returns 导入结果
     * 
     * @backend ProcessOperationController::batchImport()
     */
    batchImportOperations: (data: BatchImportOperationsForm) =>
      client.post<{ count: number }>(API_ROUTES.processOperations.batchImport, data),

    /**
     * 复制工序
     * 
     * @description
     * 复制现有工序并创建新工序。
     * 
     * @param id 要复制的工序ID
     * @param data 复制表单
     * @returns 新创建的工序信息
     * 
     * @backend ProcessOperationController::duplicate()
     */
    duplicateProcessOperation: (id: number, data: DuplicateOperationForm) =>
      client.post<ProcessOperation>(API_ROUTES.processOperations.duplicate(id), data),

    /**
     * 更新工序状态
     * 
     * @description
     * 单独更新工序的状态。
     * 
     * @param id 工序ID
     * @param data 状态更新表单
     * @returns 更新后的工序信息
     * 
     * @backend ProcessOperationController::updateStatus()
     */
    updateOperationStatus: (id: number, data: UpdateOperationStatusForm) =>
      client.put<ProcessOperation>(API_ROUTES.processOperations.update(id), data),

    /**
     * 计算产出
     * 
     * @description
     * 根据投入数量计算产出数量（考虑损耗率）。
     * 
     * @param id 工序ID
     * @param data 计算参数
     * @returns 计算结果
     * 
     * @backend ProcessOperationController::calculateOutput()
     */
    calculateOperationOutput: (id: number, data: CalculateOperationOutputForm) =>
      client.post<{ outputQuantity: number }>(API_ROUTES.processOperations.calculateOutput(id), data),

    /**
     * 计算所需投入
     * 
     * @description
     * 根据目标产出数量计算所需投入数量（考虑损耗率）。
     * 
     * @param id 工序ID
     * @param data 计算参数
     * @returns 计算结果
     * 
     * @backend ProcessOperationController::calculateRequiredInput()
     */
    calculateRequiredInput: (id: number, data: CalculateRequiredInputForm) =>
      client.post<{ inputQuantity: number }>(API_ROUTES.processOperations.calculateRequiredInput(id), data),

    // ========================================
    // 统计相关 API
    // ========================================

    /**
     * 获取工序全局统计信息
     * 
     * @description
     * 获取所有工序的全局统计信息，用于Dashboard展示。
     * 
     * @returns 全局统计信息
     * 
     * @backend ProcessOperationController::statistics()
     */
    getProcessOperationStatistics: () =>
      client.get<ProcessOperationGlobalStatistics>(API_ROUTES.processOperations.statistics),

    /**
     * 获取单个工序统计信息
     * 
     * @description
     * 获取指定工序的详细统计信息。
     * 
     * @param id 工序ID
     * @returns 工序统计信息
     * 
     * @backend ProcessOperationController::operationStatistics()
     */
    getProcessOperationDetailStatistics: (id: number) =>
      client.get<ProcessOperationStatistics>(API_ROUTES.processOperations.processOperationsStatistics(id)),
  }
}
